import React, { Component } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import ReactResizeDetector from 'react-resize-detector';
import Fa from 'react-fontawesome';
import { AgGridReact } from 'ag-grid-react';
import {
  Button,
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';
import { connect} from 'react-redux';
import { getViewableDimensions } from '../../../../App/AppReducer';
import Veil from '../../../../../components/god-table/veil';
import NumberRenderer from '../../../../../components/table-cells/NumberRenderer';
import CustomHeader from '../../../../../components/god-table/header';

class StatsTable extends Component {
  constructor(props) {
    super(props);

    this.state = {
      columnDefs: [],
      rowData: []
    };
    this.toggleFullScreenMode = this.toggleFullScreenMode.bind(this);
    this.onResizeTopBar = this.onResizeTopBar.bind(this);
  }

  componentDidMount() {
    const columnDefs = this.createColumnDefs();
    const rowData = this.props.data;

    this.setState({ columnDefs, rowData });
  }

  toggleFullScreenMode() {
    this.setState({ fullScreenMode: !this.state.fullScreenMode }, () => { });
  }

  onResizeTopBar(w, topBarHeight) {
    this.setState({ topBarHeight });
  }

  createColumnDefs() {
    return [
      {
        headerName: 'Month',
        field: 'time',
        valueFormatter: (params) => {
          const date = moment(params.value);
          return date.format('MMM YYYY');
        },
        pinned: 'left',
      },
      {
        headerName: 'Active User',
        children: [
          { 
            headerName: 'Admins', 
            valueGetter: params => params.data['users.admin'],
            cellRenderer: NumberRenderer,
            cellRendererParams: {
              column: {
                description: 'Count of admin users with an "Active Salesforce License"',
              },
            }
          },
          { 
            headerName: 'Managers', 
            valueGetter: params => params.data['users.salesManager'],
            cellRenderer: NumberRenderer,
            cellRendererParams: {
              column: {
                description: 'Count of manager users with an "Active Salesforce License"',
              },
            }
          },
          { 
            headerName: 'Sales Reps', 
            valueGetter: params => params.data['users.salesRep'],
            cellRenderer: NumberRenderer,
            cellRendererParams: {
              column: {
                description: 'Count of Sales Reps with an "Active Salesforce License"',
              },
            }
          },
        ],
      },
      {
        headerName: 'Access by Unique User',
        children: [
          { 
            headerName: 'Admins', 
            valueGetter: params => params.data['access.admin'], 
            cellRenderer: NumberRenderer,
            cellRendererParams: {
              column: {
                description: 'Count of admin users that logged into TruSales in the period',
              },
            }
          },
          { 
            headerName: 'Managers', 
            valueGetter: params => params.data['access.salesManager'], 
            cellRenderer: NumberRenderer,
            cellRendererParams: {
              column: {
                description: 'Count of managers that logged into TruSales in the period',
              },
            }
          },
          { 
            headerName: 'Sales Reps', 
            valueGetter: params => params.data['access.salesRep'], 
            cellRenderer: NumberRenderer,
            cellRendererParams: {
              column: {
                description: 'Count of sales reps that logged into TruSales in the period',
              },
            }
          }
        ]
      },
      {
        headerName: 'Accounts with Closed Opportunities',
        valueGetter: params => params.data.accounts,
        cellRenderer: NumberRenderer,
        width: 300,
        cellRendererParams: {
          column: {
            description: 'Count of accounts with opportunities that closed in the period across all clients',
          },
        }

      },
      {
        headerName: 'Active Client',
        valueGetter: params => params.data.clients,
        cellRenderer: NumberRenderer,
        cellRendererParams: {
          column: {
            description: 'Count of clients that have an active sync user',
          },
        }
      },
      {
        headerName: 'Bot Client',
        valueGetter: params => params.data.bots,
        cellRenderer: NumberRenderer,
        cellRendererParams: {
          column: {
            description: 'Count of clients that use feedback request bots in the period',
          },
        }
      },
      {
        headerName: 'Closed Opportunities by Outcome',
        headerTooltip: 'Count of opportunities that closed in the period by outcome type across all clients',
        children: [
          { 
            headerName: 'Win', 
            valueGetter: params => params.data['opportunities.outcome.win'], 
            cellRenderer: NumberRenderer,
            cellRendererParams: {
              column: {
                description: 'Count of Won opportunities that closed in the period across all clients',
              },
            }
          },
          { 
            headerName: 'Loss',
            valueGetter: params => params.data['opportunities.outcome.lose'],
            cellRenderer: NumberRenderer,
            cellRendererParams: {
              column: {
                description: 'Count of Lost opportunities that closed in the period across all clients',
              },
            }
          },
          { 
            headerName: 'No decision', 
            valueGetter: params => params.data['opportunities.outcome.noDecision'], 
            cellRenderer: NumberRenderer,
            cellRendererParams: {
              column: {
                description: 'Count of No Decision opportunities that closed in the period across all clients',
              },
            }
          }
        ]
      },
      {
        headerName: 'Closed Opportunities by Type',
        children: [
          { 
            headerName: 'New business', 
            valueGetter: params => params.data['opportunities.type.new'], 
            cellRenderer: NumberRenderer,
            cellRendererParams: {
              column: {
                description: 'Count of New Bussiness opportunities that closed in the period across all clients',
              },
            },
          },
          { 
            headerName: 'Renewal', 
            valueGetter: params => params.data['opportunities.type.renewal'], 
            cellRenderer: NumberRenderer,
            cellRendererParams: {
              column: {
                description: 'Count of Renewal opportunities that closed in the period across all clients',
              },
            },
          },
          { 
            headerName: 'Addon', 
            valueGetter: params => params.data['opportunities.type.addon'], 
            cellRenderer: NumberRenderer,
            cellRendererParams: {
              column: {
                description: 'Count of Addon opportunities that closed in the period across all clients',
              },
            },
          },
          { 
            headerName: 'Upsell', 
            valueGetter: params => params.data['opportunities.type.upsell'], 
            cellRenderer: NumberRenderer,
            cellRendererParams: {
              column: {
                description: 'Count of Upsell opportunities that closed in the period across all clients',
              },
            },
          }
        ]
      },
      {
        headerName: 'Closed Opportunities by Size',
        
        children: [
          { 
            headerName: 'Small', 
            valueGetter: params => params.data['opportunities.size.small'], 
            cellRenderer: NumberRenderer,
            cellRendererParams: {
              column: {
                description: 'Count of opportunities with amount between $1 and $100,000 USD that closed in the period',
              },
            },
          },
          { 
            headerName: 'Medium', 
            valueGetter: params => params.data['opportunities.size.medium'], 
            cellRenderer: NumberRenderer,
            cellRendererParams: {
              column: {
                description: 'Count of opportunities with amount between $100,000 and $1,000,000 USD that closed in the period',
              },
            },
          },
          { 
            headerName: 'Large', 
            valueGetter: params => params.data['opportunities.size.large'], 
            cellRenderer: NumberRenderer,
            cellRendererParams: {
              column: {
                description: 'Count of opportunities with amount greater than $1M USD that closed in the period',
              },
            },
          }
        ]
      },
    ];
  }

  renderFullScreenToggle() {
    const { fullScreenMode } = this.state;
    const description = fullScreenMode
      ? 'Exit Full Screen'
      : 'Enter Full Screen';
    return (
      <span>
        &nbsp;&nbsp;
        <OverlayTrigger
          placement={fullScreenMode ? 'bottom' : 'top'}
          overlay={<Tooltip id='full-screen-tip'>{description}</Tooltip>}>
          <Button bsStyle='link' onClick={this.toggleFullScreenMode}>
            <Fa name={fullScreenMode ? 'compress' : 'expand'} />
          </Button>
        </OverlayTrigger>
      </span>
    );
  }

  render() {
    const { viewableDimensions } = this.props;
    const tHeight = viewableDimensions.height || '500px';
    const { disableShowFullScreenMode, fullScreenMode, topBarHeight } = this.state;
    const tableHeight = fullScreenMode
    ? Math.max(tHeight - topBarHeight, 100)
    : tHeight;
    return (
      <div>
        <Veil anchorElement={this._el} fullScreenMode={this.state.fullScreenMode}>
        <nav className='navbar navbar-default navbar-controls'>
          <div
            className='navbar-form navbar-right'
            style={{ paddingRight: 30 }}>

            {!disableShowFullScreenMode && this.renderFullScreenToggle()}
          </div>
          <ReactResizeDetector
            handleHeight
            onResize={this.onResizeTopBar}
          />
        </nav>
        <div
          className="ag-theme-alpine"
          style={{ height: tableHeight, width: '100%' }}
        >
          <AgGridReact
            columnDefs={this.state.columnDefs}
            rowData={this.state.rowData}
            enableColResize
            components={{
              agColumnHeader: CustomHeader,
            }}
          >
          </AgGridReact>
        </div>
        </Veil>
      </div>
    
    );
  }
}

function mapStateToProps(state) {
  return {
    viewableDimensions: getViewableDimensions(state),
  };
}

StatsTable.propTypes = {
  data: PropTypes.array.isRequired,
  viewableDimensions: PropTypes.shape({
    height: PropTypes.number,
  }).isRequired,
};

export default connect(mapStateToProps)(StatsTable);
